import * as React from 'react';
import type { Color } from '@chess/api-model';
import { glyph, type Loss, type PlayerMaterial as PlayerMaterialData } from './material.ts';

// The material on a player's line beside the board (review-board's
// `review.player.material` slot): the pieces that player has lost so far as
// glyphs of their colour, one per piece, grouped by kind with pawns first,
// and, on the line of the side that is ahead, the lead in pawns ("+1")
// after them. Nothing on the other line, and nothing at all while the
// material is level and untouched. Presentational: the numbers come from
// `materialOf` (material.ts).

export type PlayerMaterialProps = PlayerMaterialData;

const PIECE_NAME: Record<Loss['kind'], string> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen' };

/** "lost 2 pawns, 1 knight; 1 pawn ahead" for assistive technology; the glyphs are hidden from it. */
export function materialLabel(material: PlayerMaterialData): string {
  const lost = material.lost.length === 0 ? 'lost nothing' : `lost ${material.lost.map((l) => `${l.count} ${PIECE_NAME[l.kind]}${l.count === 1 ? '' : 's'}`).join(', ')}`;
  return material.lead === undefined ? lost : `${lost}; ${material.lead} ${material.lead === 1 ? 'pawn' : 'pawns'} ahead`;
}

const Pieces: React.FC<{ lost: Loss[]; color: Color }> = ({ lost, color }) => (
  <span className="player-material-pieces" aria-hidden="true">
    {lost.map((loss) => (
      <span key={loss.kind} className="player-material-kind">
        {glyph(loss.kind, color).repeat(loss.count)}
      </span>
    ))}
  </span>
);

export const PlayerMaterial: React.FC<PlayerMaterialProps> = (material) => {
  if (material.lost.length === 0 && material.lead === undefined) return null;
  return (
    <span className="player-material" role="img" aria-label={materialLabel(material)}>
      {material.lost.length > 0 && <Pieces lost={material.lost} color={material.color} />}
      {material.lead !== undefined && <span className="player-material-lead">+{material.lead}</span>}
    </span>
  );
};
